import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { updateMeOptionsSchema } from '@airreading/common/data';
import { isCoach } from '@airreading/common';

import { EditableInfoPage, SubmitProps } from '@/components/editable-info-page.js';
import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { comboBoxOptionsOf } from '@/components/ui/multi-select-utils.js';
import { RoleBadgeList } from '@/components/users/role-badge.js';
import { getMe, updateMe } from '@/lib/api-calls.js';
import { AllTimeZones, getTimezoneTypeahead, getTimeZone, timezoneZodSchema } from '@/lib/time-zones.js';
import { ProfileFormProps } from './profile-form.js';

const formSchema = updateMeOptionsSchema.extend({
  timeZone: timezoneZodSchema,
});
type FormState = z.infer<typeof formSchema>;

export const BasicProfileForm = ({ toast, queryClient, setTimezone, getSubmitErrorMessage }: ProfileFormProps) => {
  const { mutateAsync } = useMutation({
    mutationFn: updateMe,
  });
  const { data: profile } = useSuspenseQuery(getMe());
  const currentTimeZone = getTimeZone(profile.timeZone);

  const onSubmit = ({ formData, disableEditing }: SubmitProps<FormState>) => {
    return mutateAsync({
      ...formData,
      timeZone: formData.timeZone.value,
    })
      .then(({ timeZone }) => {
        if (timeZone) {
          setTimezone(timeZone);
        }

        toast({
          title: 'Profile updated successfully',
        });
        disableEditing();
        queryClient.invalidateQueries({ queryKey: getMe().queryKey });
      })
      .catch((e: unknown) => {
        toast({
          title: getSubmitErrorMessage(e),
        });
      });
  };

  return (
    <EditableInfoPage
      headerText={profile.fullNameOrEmail}
      formSchema={formSchema}
      defaultValues={{
        timeZone: {
          value: currentTimeZone,
          label: AllTimeZones.find((tz) => tz.value === currentTimeZone)?.label || currentTimeZone,
        },
        phone: profile.phone || '',
      }}
      onSubmit={onSubmit}
    >
      {({ form, editing }) => {
        return (
          <>
            <InfoCard headerText="Profile">
              <InfoKeyValue label="First name" value={profile.firstName} />
              <InfoKeyValue label="Last name" value={profile.lastName} />
              <InfoKeyValue label="Email" value={profile.email} />
              <InfoKeyValue
                label="Phone number"
                value={profile.phone}
                edit={{
                  editing,
                  editModeComponent: <FormInputField label="Phone number" name="phone" form={form} />,
                }}
              />
              <InfoKeyValue
                label="Time zone"
                value={profile.timeZone}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormComboBox
                      name="timeZone"
                      label="Time zone"
                      form={form}
                      {...comboBoxOptionsOf(getTimezoneTypeahead)}
                      triggerProps={{ className: 'w-2/3' }}
                      required
                    />
                  ),
                }}
              />
              <InfoKeyValue
                label="Roles"
                value={
                  <div className="flex gap-x-1">
                    <RoleBadgeList userRoles={profile.userRoles} hideEdgeCoachTag={isCoach(profile)} />
                  </div>
                }
              />
            </InfoCard>

            <InfoCard headerText="Zoom information">
              <InfoKeyValue label="Zoom URL" value={profile.zoomPersonalMeetingUrl} />
            </InfoCard>
          </>
        );
      }}
    </EditableInfoPage>
  );
};
